import { useEffect, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button } from '@mui/material'
import WTypography from '../../../../Components/whiteTypography'

// LAP-752 — Canonical order of the 4 ecological categories. Used both
// for the donut slice order AND for the empty-state CTAs so the user
// always sees them in the same sequence as the dashboard cards
// (Logement → Transport → Shopping → Alimentation).
const ECO_CATEGORIES = [
  { key: 'Logement', icon: '🏠', to: '/domicile/monimmobilier' },
  { key: 'Transport', icon: '🚗', to: '/domicile/mesvehicules' },
  { key: 'Shopping', icon: '🛍', to: '/shopping' },
  { key: 'Alimentation', icon: '🍽', to: '/Dashboard?questionnaire=alimentation' },
]

function DonutChartEmission({ reports, setHeaderValue, fetchMesActifs }) {
  const navigate = useNavigate()
  const { t } = useTranslation('layout')
  const [emptyReport, setEmptyReport] = useState(false)
  const [revData, setRevData] = useState({
    labels: ['Revenus salariaux', 'Revenus passifs', 'revenus autres'],
    datasets: [
      {
        data: [80, 5, 15],
        backgroundColor: ['#93b7ff', '#6087cd', '#295a9c'],
        borderColor: ['#93b7ff', '#6087cd', '#295a9c'],
        borderWidth: 1,
        hoverOffset: 4,
        precision: 0,
      },
    ],
  })
  const optiondonut = {
    responsive: true,
    aspectRatio: 1.3,
    clip: true,

    plugins: {
      datalabels: {
        formatter: function (value, ctx) {
          return ctx.chart.data.labels[ctx.dataIndex] + '\n' + [value + '%']
        },
        textAlign: 'center',
        color: 'white',
        anchor: 'center',
        clamp: true,
        display: 'auto',
      },

      legend: {
        display: false,
        events: [],
      },
      tooltips: {
        callbacks: {
          label: function (tooltipItem) {
            return tooltipItem.yLabel
          },
        },
      },
    },
  }
  useEffect(() => {
    // LAP-752 — Order is intentionally fixed (Logement, Transport,
    // Shopping, Alimentation) to match the dashboard card layout above.
    const groupName = ECO_CATEGORIES.map((c) => c.key)
    let amount = [0, 0, 0, 0]
    let percentageAmount = []

    if (reports.length > 0) {
      let item = reports[0]
      amount = [
        parseFloat(item.emission_logement) || 0,
        parseFloat(item.emission_transport) || 0,
        parseFloat(item.emission_shopping) || 0,
        parseFloat(item.emission_alimentation) || 0,
      ]
    }

    // Calculate total as sum of all emission categories (sum of each card)
    let totalAmount = amount.reduce((a, b) => a + b, 0)
    
    if (totalAmount > 0) {
      setEmptyReport(false)
      // Calculate percentage for each category
      percentageAmount = amount.map((val) => {
        return ((val * 100) / totalAmount).toFixed(0)
      })
    } else {
      setEmptyReport(true)
      // Set all to 0 if no data
      percentageAmount = ['0', '0', '0', '0']
    }

    setRevData({
      labels: groupName,
      datasets: [
        {
          data: percentageAmount,
          backgroundColor: ['#a7c1f5', '#668bcf', '#3d6dad', '#274889'],
          borderColor: ['#a7c1f5', '#668bcf', '#3d6dad', '#274889'],
          borderWidth: 1,
          hoverOffset: 4,
          precision: 0,
        },
      ],
    })
  }, [reports])

  const RevDoughnutChart = () => (
    <>
      {emptyReport ? (
        // LAP-752 — Replace the bare "Aucune donnée" message with an
        // actionable empty state. Shows each of the 4 categories in the
        // canonical dashboard order so the user understands what to fill
        // and can jump directly to the matching questionnaire.
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          mt={4}
          mb={2}
          px={1}
          className="no-drag"
        >
          <WTypography
            variant="h6"
            sx={{ color: '#ffffff', fontWeight: 700, mb: 0.5, fontSize: 16 }}
          >
            {t('dashboard.donutEmptyTitle', 'Aucune donnée à répartir')}
          </WTypography>
          <WTypography
            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, maxWidth: 260, mb: 2 }}
            variant="body2"
          >
            {t(
              'dashboard.donutEmptyDesc',
              'Renseignez vos catégories pour voir la répartition de votre empreinte.'
            )}
          </WTypography>
          <Box display="flex" flexWrap="wrap" gap={0.75} justifyContent="center">
            {ECO_CATEGORIES.map((c) => (
              <Button
                key={c.key}
                size="small"
                onClick={() => navigate(c.to)}
                sx={{
                  textTransform: 'none',
                  color: '#dbe5f7',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  fontSize: 12,
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.4,
                  '&:hover': {
                    background: 'rgba(47,210,138,0.18)',
                    borderColor: 'rgba(47,210,138,0.5)',
                  },
                }}
              >
                {c.icon} {t(`dashboard.cat${c.key}`, c.key)}
              </Button>
            ))}
          </Box>
        </Box>
      ) : (
        <Doughnut data={revData} options={optiondonut} />
      )}
    </>
  )

  return (
    <>
      <RevDoughnutChart />
    </>
  )
}

export default DonutChartEmission
